/**
 * Drag a row onto another row of the SAME day, to reorder within that day.
 *
 * Both registers do this — the account register and the wallet log — and
 * they did it with two copies of the same listeners. One of the copies was
 * written twice as long after the other, which is how the wallet log came to
 * send only the rows it was showing.
 *
 * **Crossing days is refused during the drag, not after it.** A row of
 * another day never calls `preventDefault`, so the browser shows the "no
 * drop" cursor and no drop event happens. The date decides where an entry
 * sits; `seq` only orders what happens inside that day, so a drag across
 * days would silently re-date a transaction — a change to the books
 * disguised as a layout gesture. The server checks the same rule rather than
 * trusting this.
 */

export interface DayDragOptions {
  /** The element the rows live in. Re-wired on every render. */
  container: HTMLElement;
  /** Which elements are draggable rows. */
  rowSelector: string;
  /** The record a row stands for, and the day it is on. */
  idOf: (row: HTMLElement) => string | undefined;
  dateOf: (row: HTMLElement) => string | undefined;
  /** Called once per completed drop, with ids in the order they were shown. */
  onMove: (date: string, movedId: string, targetId: string) => void;
  /** Marks the row currently under the pointer. */
  markClass?: string;
}

export function wireDayDrag(opts: DayDragOptions): void {
  const mark = opts.markClass ?? 'wl-drop-target';
  let dragging: { id: string; date: string } | undefined;

  const clearMarks = (): void => {
    opts.container.querySelectorAll(`.${mark}`).forEach((el) => el.classList.remove(mark));
  };

  for (const row of opts.container.querySelectorAll<HTMLElement>(opts.rowSelector)) {
    const id = opts.idOf(row);
    const date = opts.dateOf(row);
    // A row that cannot say what it is or when it is cannot take part. Better
    // inert than dragged into a reorder that names the wrong record.
    if (id === undefined || date === undefined) continue;

    row.addEventListener('dragstart', (event) => {
      dragging = { id, date };
      const dt = event.dataTransfer;
      dt?.setData('text/plain', id);
      if (dt) dt.effectAllowed = 'move';
    });

    row.addEventListener('dragover', (event) => {
      if (!dragging || date !== dragging.date) return;
      event.preventDefault();
      row.classList.add(mark);
    });

    row.addEventListener('dragleave', () => row.classList.remove(mark));

    row.addEventListener('drop', (event) => {
      row.classList.remove(mark);
      if (!dragging || date !== dragging.date) return;
      event.preventDefault();
      const moved = dragging;
      dragging = undefined;
      opts.onMove(moved.date, moved.id, id);
    });

    row.addEventListener('dragend', () => {
      dragging = undefined;
      clearMarks();
    });
  }
}
